import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject, type Ref, type RefCallback } from 'react';

export function useToggle(initial = false): [boolean, () => void, (value: boolean) => void] {
  const [value, setValue] = useState(initial);
  const toggle = useCallback(() => setValue((v) => !v), []);
  return [value, toggle, setValue];
}

export interface UseDisclosureHandlers {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export interface UseDisclosureCallbacks {
  onOpen?: () => void;
  onClose?: () => void;
}

/** Boolean state with open/close/toggle, plus optional change callbacks. */
export function useDisclosure(
  initial = false,
  callbacks: UseDisclosureCallbacks = {}
): [boolean, UseDisclosureHandlers] {
  const { onOpen, onClose } = callbacks;
  const [opened, setOpened] = useState(initial);

  const open = useCallback(() => {
    setOpened((prev) => {
      if (prev) return prev;
      onOpen?.();
      return true;
    });
  }, [onOpen]);

  const close = useCallback(() => {
    setOpened((prev) => {
      if (!prev) return prev;
      onClose?.();
      return false;
    });
  }, [onClose]);

  const toggle = useCallback(() => {
    if (opened) close();
    else open();
  }, [opened, open, close]);

  return [opened, { open, close, toggle }];
}

export interface UseUncontrolledOptions<T> {
  value?: T;
  defaultValue?: T;
  finalValue?: T;
  onChange?: (value: T) => void;
}

/** Bridge controlled/uncontrolled value props; returns [value, setValue, isControlled]. */
export function useUncontrolled<T>({
  value,
  defaultValue,
  finalValue,
  onChange
}: UseUncontrolledOptions<T>): [T, (next: T) => void, boolean] {
  const [uncontrolled, setUncontrolled] = useState<T>(
    defaultValue !== undefined ? defaultValue : (finalValue as T)
  );

  const isControlled = value !== undefined;
  const current = isControlled ? (value as T) : uncontrolled;

  const setValue = useCallback(
    (next: T) => {
      if (!isControlled) setUncontrolled(next);
      onChange?.(next);
    },
    [isControlled, onChange]
  );

  return [current, setValue, isControlled];
}

/** Combine multiple React refs (callback or object) into a single callback ref. */
export function useMergedRef<T>(...refs: Array<Ref<T> | undefined | null>): RefCallback<T> {
  return useMemo(
    () => (node: T | null) => {
      for (const ref of refs) {
        if (!ref) continue;
        if (typeof ref === 'function') {
          ref(node);
        } else {
          (ref as MutableRefObject<T | null>).current = node;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refs
  );
}

export interface UseAsyncResult<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  reload: () => void;
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): UseAsyncResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [seq, setSeq] = useState(0);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    setLoading(true);
    fn().then(
      (value) => {
        if (!aliveRef.current) return;
        setData(value);
        setError(null);
        setLoading(false);
      },
      (e) => {
        if (!aliveRef.current) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
      }
    );
    return () => { aliveRef.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seq, ...deps]);

  const reload = useCallback(() => setSeq((s) => s + 1), []);
  return { data, error, loading, reload };
}
